import type { MoodScaleData, ScaleSelectorProps } from "@qendo/profile/journal";
import {
    type Mood,
    NEGATIVE_FEELINGS,
    NEGATIVE_MOODS,
    POSITIVE_FEELINGS,
    POSITIVE_MOODS,
    type ScaleType,
} from "@qendo/trackables";
import { useMemo } from "react";
import { View } from "react-native";
import { Chip } from "react-native-paper";
import { FieldGroup } from "~/components/form/FieldGroup";
import { FieldLabel } from "~/components/form/FieldLabel";
import { MoodPicker } from "~/components/form/radio/MoodPicker";
import { Flex, Gap, Margin } from "~/constants/layout";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { randomString } from "~/util/string";

export type MoodSelectorProps = ScaleSelectorProps<ScaleType.mood>;

const isPositive = (mood: Mood | null): boolean => Boolean(mood && POSITIVE_MOODS.includes(mood));
const isNegative = (mood: Mood | null): boolean => Boolean(mood && NEGATIVE_MOODS.includes(mood));

export const MoodSelector = ({ value, onUpdateScale, disabled, zIndex }: MoodSelectorProps) => {
    const { colours } = useTheme();
    const mood = value.value;
    const feelings = value.feelings;

    const possible_feelings = useMemo(
        () =>
            ((isPositive(mood) ? POSITIVE_FEELINGS : null) ?? (isNegative(mood) ? NEGATIVE_FEELINGS : null) ?? []).map(
                (feeling) => ({ feeling, key: randomString() }),
            ),
        [mood],
    );

    const changeMood = (m: Mood) => {
        if (mood !== m) {
            onUpdateScale({
                ...value,
                value: m,
                feelings: (isPositive(mood) && isPositive(m)) || (isNegative(mood) && isNegative(m)) ? feelings : [],
            } as MoodScaleData);
        }
    };

    const toggleFeeling = (feeling: string) => {
        const new_feelings = feelings.includes(feeling)
            ? feelings.filter((f) => f !== feeling)
            : [...feelings, feeling];
        new_feelings.sort();
        onUpdateScale({ ...value, feelings: new_feelings } as MoodScaleData);
    };

    return (
        <>
            <FieldGroup zIndex={zIndex}>
                <MoodPicker label="Which best represents your mood?" changeMood={changeMood} value={mood} />
            </FieldGroup>
            {filled(possible_feelings) && (
                <FieldGroup zIndex={zIndex}>
                    <FieldLabel label="What are you feeling?" optional>
                        <View style={[Flex.row, Flex.wrap, Gap.xs, Margin.top_xxs]}>
                            {possible_feelings.map((f) => (
                                <Chip
                                    key={f.key}
                                    mode="outlined"
                                    onPress={() => toggleFeeling(f.feeling)}
                                    selected={feelings.includes(f.feeling)}
                                    disabled={disabled}
                                    showSelectedOverlay
                                    showSelectedCheck={false}
                                    selectedColor={colours.text.selected}
                                    textStyle={{
                                        color: feelings.includes(f.feeling)
                                            ? colours.button.secondary
                                            : colours.text.primary,
                                    }}
                                    style={{
                                        backgroundColor: feelings.includes(f.feeling)
                                            ? colours.button["secondary-background"]
                                            : "transparent",
                                    }}
                                >
                                    {f.feeling}
                                </Chip>
                            ))}
                        </View>
                    </FieldLabel>
                </FieldGroup>
            )}
        </>
    );
};
